/**
 * MoSCoW Method tool runner.
 * Categorizes items into Must, Should, Could, or Won't categories.
 */

import type {
  Item,
  CriteriaSet,
  ToolConfig,
  Lens,
} from "../harmonizer/types";
import type { NativeScoreSet } from "../harmonizer/testHarness";
import type { RawToolSpec } from "../harmonizer/specLoader";
import { loadToolSpecFile, getToolSpecPath } from "./specLoader";

let cachedSpec: RawToolSpec | null = null;

/**
 * Gets the raw tool spec for MoSCoW.
 */
export function getMoscowSpec(): RawToolSpec {
  if (cachedSpec) {
    return cachedSpec;
  }

  const specPath = getToolSpecPath("moscow-method");
  cachedSpec = loadToolSpecFile(specPath);
  return cachedSpec;
}

/**
 * Runs the MoSCoW categorization tool.
 * 
 * Note: This implementation assumes items have a category assignment in their attributes.
 * In a real system, categories would be assigned through UI interaction.
 * For this runner, we look for item.attributes.category or item.attributes.moscow_category.
 * 
 * @param items - Items to categorize
 * @param _criteriaSet - CriteriaSet (not used by MoSCoW)
 * @param _toolConfig - Tool configuration (not used)
 * @param lens - Optional lens for filtering
 * @returns Native score set
 */
export async function runMoscowTool(
  items: Item[],
  _criteriaSet: CriteriaSet | null,
  _toolConfig: ToolConfig,
  lens?: Lens | null
): Promise<NativeScoreSet> {
  const toolRunId = `moscow-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  // Filter items if lens is provided
  let filteredItems = items;
  if (lens) {
    const { applyLens } = await import("../arrange/validation");
    filteredItems = applyLens(items, lens);
  }

  // Valid categories
  const validCategories = ["Must", "Should", "Could", "Wont"];
  const categoryRanks: Record<string, number> = {
    "Must": 1,
    "Should": 2,
    "Could": 3,
    "Wont": 4,
  };

  const categorizedItems: Array<{
    item_id: string;
    category: string;
    category_rank: number;
  }> = [];
  const itemsWithCategory: Array<{ item: Item; category: string; categoryRank: number }> = [];

  for (const item of filteredItems) {
    // Look for category in attributes
    const category =
      item.attributes.category ||
      item.attributes.moscow_category ||
      item.attributes.moscow;

    if (typeof category !== "string" || !validCategories.includes(category)) {
      // Skip items without valid category assignment
      continue;
    }

    const categoryRank = categoryRanks[category];
    itemsWithCategory.push({ item, category, categoryRank });
  }

  // Sort by category rank (ascending), then by label (ascending) for tie-breaking
  itemsWithCategory.sort((a, b) => {
    if (a.categoryRank !== b.categoryRank) {
      return a.categoryRank - b.categoryRank;
    }
    return a.item.label.localeCompare(b.item.label);
  });

  // Build output
  for (const { item, category, categoryRank } of itemsWithCategory) {
    categorizedItems.push({
      item_id: item.id,
      category: category,
      category_rank: categoryRank,
    });
  }

  return {
    items: categorizedItems,
    toolRunId,
    metadata: {
      total_items: filteredItems.length,
      categorized_items: categorizedItems.length,
    },
  };
}


